import moment from 'moment'

import { Frequency } from './ScheduleControls/ScheduleControls'

export const convertScheduleDate = (date: string) => {
  const datePieces = date.split('-')

  return new Date(
    parseInt(datePieces[0]),
    parseInt(datePieces[1]) - 1,
    parseInt(datePieces[2]),
  )
}

export const getTimePieces = (time: string): number[] => {
  return time.split(':').map((piece: string) => parseInt(piece))
}

export const isWithin24Hours = (earlierDate: Date, laterDate: Date) => {
  const fullDayInMS = 24 * 60 * 60 * 1000
  const msDifference = laterDate.getTime() - earlierDate.getTime()

  return msDifference <= fullDayInMS
}

export const getNextScan = (
  frequency: string,
  start_date: Date | string,
  till_date: Date | string,
  time: string,
) => {
  if (!time) {
    return {
      nextScan: undefined,
      nextScanFormatted: '',
      isExpired: false,
      lessThan24HoursAway: false,
    }
  }

  const today = new Date()
  today.setSeconds(0, 0)
  const timePieces = getTimePieces(time)

  const convertedTillDate =
    typeof till_date === 'string' ? convertScheduleDate(till_date) : till_date
  convertedTillDate.setHours(timePieces[0], timePieces[1], 0)

  //if the till date is in the past, return the last scan and note the scan has expired
  if (convertedTillDate < today) {
    return {
      nextScan: getFinalScan(frequency, start_date, till_date, time),
      nextScanFormatted: 'expired',
      isExpired: true,
      lessThan24HoursAway: true,
    }
  }

  const convertedStartDate =
    typeof start_date === 'string'
      ? convertScheduleDate(start_date)
      : start_date
  convertedStartDate.setHours(timePieces[0], timePieces[1], 0)

  const startDatePast: boolean = convertedStartDate.getTime() < today.getTime()

  //today's date with the schedule time
  let nextScan = new Date(today)
  nextScan.setHours(timePieces[0], timePieces[1], 0)

  if (!startDatePast) {
    nextScan = convertedStartDate
  } else if (frequency === Frequency.weekly) {
    const scheduleDayOfWeek = convertedStartDate.getDay()
    const todaysDayOfWeek = today.getDay()

    const daysFromNow =
      todaysDayOfWeek <= scheduleDayOfWeek
        ? scheduleDayOfWeek - todaysDayOfWeek
        : scheduleDayOfWeek - todaysDayOfWeek + 7
    nextScan.setDate(nextScan.getDate() + daysFromNow)

    if (nextScan.getTime() < today.getTime()) {
      nextScan.setDate(nextScan.getDate() + 7)
    }
  } else if (frequency === Frequency.monthly) {
    const scheduleDayOfMonth = convertedStartDate.getDate()
    const todaysDayOfMonth = today.getDate()

    if (todaysDayOfMonth < scheduleDayOfMonth) {
      nextScan.setDate(scheduleDayOfMonth)
    } else if (todaysDayOfMonth !== scheduleDayOfMonth || nextScan < today) {
      nextScan.setDate(scheduleDayOfMonth)
      nextScan.setMonth(nextScan.getMonth() + 1)
    }
  }

  const nextScanFormatted: string = moment(nextScan).format(
    'YYYY-MM-DD [at] h:mm A',
  )

  return {
    nextScan: nextScan,
    nextScanFormatted: nextScanFormatted,
    isExpired: false,
    lessThan24HoursAway: isWithin24Hours(today, nextScan),
  }
}

export const getFinalScan = (
  frequency: string,
  start_date: Date | string,
  till_date: Date | string,
  _time: string,
) => {
  const convertedTillDate =
    typeof till_date === 'string' ? convertScheduleDate(till_date) : till_date
  const convertedStartDate =
    typeof start_date === 'string'
      ? convertScheduleDate(start_date)
      : start_date

  const finalScan = new Date(convertedTillDate)
  const tillDateDayOfWeek = convertedTillDate.getDay()
  const tillDateDayOfMonth = convertedTillDate.getDate()

  if (frequency === Frequency.weekly) {
    const scanDayOfWeek = convertedStartDate.getDay()

    if (tillDateDayOfWeek !== scanDayOfWeek) {
      const daysBack =
        scanDayOfWeek < tillDateDayOfWeek
          ? tillDateDayOfWeek - scanDayOfWeek
          : tillDateDayOfWeek - scanDayOfWeek + 7
      finalScan.setDate(finalScan.getDate() - daysBack)
    }
  } else if (frequency === Frequency.monthly) {
    const scanDayOfMonth = convertedStartDate.getDate()

    if (tillDateDayOfMonth !== scanDayOfMonth) {
      finalScan.setDate(scanDayOfMonth)

      if (scanDayOfMonth > tillDateDayOfMonth) {
        finalScan.setMonth(finalScan.getMonth() - 1)
      }
    }
  }

  return finalScan
}
